import { Currency } from '@/modules/common/types/currency';

import { MembershipType } from './membership-type';
import { MemberStatus } from './team-member';

/**
 * Domain enum for patch effect actions
 * Reused from projects module for consistency
 */
export enum PatchEffectAction {
  Delete = 'delete',
  Cut = 'cut',
}

/**
 * Domain model for patch entity effect
 * Describes the impact of a patch operation on an existing entity change
 */
export interface PatchEntityEffect {
  action: PatchEffectAction;
  cutEffectiveStartDate: string | null;
  cutEffectiveEndDate: string | null;
}

/**
 * Simplified reference type for entities in conflict data
 * Contains only id and name for display purposes
 */
export interface ConflictEntityReference {
  id: number;
  name: string;
}

/**
 * Domain model for team member versioned fields
 * Contains all fields that can change over time
 */
export interface TeamMemberVersionedFields {
  // Info section
  status: MemberStatus;
  department: ConflictEntityReference | null;
  role: ConflictEntityReference | null;
  seniority: ConflictEntityReference | null;
  country: string | null;
  city: string | null;
  manager: ConflictEntityReference | null;
  membershipType: MembershipType | null;
  isManager: boolean;

  // Capacity section
  isOnDemand: boolean;
  totalWorkingMinutes: number;
  billableCapacity: number;

  // Billing section
  isHourlyRated: boolean;
  currency: Currency;
  internalRate: number | null;
  externalRate: number | null;
}

/**
 * Domain model for a single team member conflict snapshot
 * Represents the state of a team member at a specific time period
 */
export interface TeamMemberConflictSnapshot {
  id: number;
  snapshot: TeamMemberVersionedFields;
  patchEffect: PatchEntityEffect;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  description: string;
}

/**
 * Generic conflict resolution data
 * Contains list of conflict snapshots
 */
export interface TeamMemberConflictResolutionData {
  items: TeamMemberConflictSnapshot[];
  hasConflicts: boolean;
}

/**
 * Parameters for checking team member conflicts
 * Used by all conflict checking hooks (info, capacity, billing)
 */
export interface CheckConflictsParams {
  teamMemberId: string;
  startDate: string;
  endDate: string | null;
}

/**
 * Result of conflict checking operation
 * Used by all conflict checking hooks (info, capacity, billing)
 */
export interface CheckConflictsResult {
  items: TeamMemberConflictSnapshot[];
  hasConflicts: boolean;
}
